import { negate } from 'lodash-es'

import { getCoreAssetType, ProductConfig } from '../state/configuration'
import { AdditionalPriceOption, ConditionalCover, CoverOption } from './types'

export const isAddOn = (product: ProductConfig) => negate(isAuxiliary(product))
export const isAuxiliary = (product: ProductConfig) => (coverOption: CoverOption) => {
  const assetType = getCoreAssetType(product)
  return Boolean(
    assetType.covers.optional?.find((it) => it.code === coverOption.coverGroup)?.form,
  )
}

export const getUpdatedPriceOptions = (coverOption: CoverOption) =>
  coverOption.priceOptions.map((po) => ({
    ...po,
    active: coverOption.active,
  }))

export type ConditionalCoverChange = {
  type: 'ADDED' | 'REMOVED'
  cover: ConditionalCover
}

export const getConditionalCoverChange = (
  current: ConditionalCover[],
  updated: ConditionalCover[],
): ConditionalCoverChange[] => {
  const added = updated
    .filter(
      ({ active, coverCode }) =>
        active && !current.find((it) => it.coverCode === coverCode)?.active,
    )
    .map<ConditionalCoverChange>((cover) => ({
      type: 'ADDED',
      cover,
    }))

  const removed = current
    .filter(
      ({ active, coverCode }) =>
        active && !updated.find((it) => it.coverCode === coverCode)?.active,
    )
    .map<ConditionalCoverChange>((cover) => ({
      type: 'REMOVED',
      cover,
    }))

  return [...added, ...removed]
}

export type CoverOptionChange = {
  type: 'ADDED' | 'REMOVED' | 'AVAILABLE'
  cover: CoverOption
}

export const getCoverOptionChange = (
  current: CoverOption[],
  updated: CoverOption[],
): CoverOptionChange[] => {
  const added = updated
    .filter(
      ({ active, coverGroup }) =>
        active && !current.find((it) => it.coverGroup === coverGroup)?.active,
    )
    .map<CoverOptionChange>((cover) => ({
      type: 'ADDED',
      cover,
    }))

  const removed = current
    .filter(
      ({ active, coverGroup }) =>
        active && !updated.find((it) => it.coverGroup === coverGroup)?.active,
    )
    .map<CoverOptionChange>((cover) => ({
      type: 'REMOVED',
      cover,
    }))

  const available = updated
    .filter(
      ({ active, coverGroup, available }) =>
        available &&
        !active &&
        !current.find((it) => it.coverGroup === coverGroup)?.available,
    )
    .map<CoverOptionChange>((cover) => ({
      type: 'AVAILABLE',
      cover,
    }))

  return [...added, ...removed, ...available]
}

// Sync between add-on selection and additional premium breakdown (active status)
export const getUpdatedAdditionalPriceBreakdown = (
  coverOptions: CoverOption[],
  additionalPriceOptions: AdditionalPriceOption[],
) =>
  additionalPriceOptions.map((additional) => {
    const matchingCover = coverOptions.find(
      (it) => it.coverGroup === additional.coverOption,
    )

    return matchingCover
      ? { ...additional, active: Boolean(matchingCover.active) }
      : additional
  })
